/**
 * Normalizes legacy {{key}} placeholders to [[key]] so the template works with substituteUrlTemplate.
 * Use when loading a stored urlTemplate that may have been saved with the old syntax.
 */
export function normalizeUrlTemplatePlaceholders(template: string): string {
  return template.replace(/\{\{(\w+)\}\}/g, '[[$1]]')
}

/**
 * Returns the set of placeholder keys (e.g. "hideSidebar") found in the template as [[key]].
 */
export function getTemplatePlaceholderKeys(template: string): Set<string> {
  const keys = new Set<string>()
  template.replace(/\[\[(\w+)\]\]/g, (_match, key) => {
    keys.add(key)
    return ''
  })
  return keys
}

/**
 * Appends &key=[[key]] for any key in requiredKeys that is not already present as [[key]] in the template.
 * Ensures fixed/default params (e.g. kiosk) are substituted even when the stored template was saved before they were added.
 */
export function ensureTemplatePlaceholders(template: string, requiredKeys: string[]): string {
  if (requiredKeys.length === 0) { return template }
  const existing = getTemplatePlaceholderKeys(template)
  const toAdd = requiredKeys.filter(k => !existing.has(k))
  if (toAdd.length === 0) { return template }
  const sep = template.includes('?') ? '&' : '?'
  const extra = toAdd.map(k => `${encodeURIComponent(k)}=[[${k}]]`).join('&')
  return template + sep + extra
}

/**
 * Substitute [[key]] placeholders in a URL template with config values, encoding for URL safety.
 * Same placeholder syntax as widgetBuilderRuntime.substituteConfig (Vue-compatible; not {{key}}).
 */
export function substituteUrlTemplate(
  template: string,
  config: Record<string, unknown>
): string {
  const normalized = normalizeUrlTemplatePlaceholders(template)
  return normalized.replace(/\[\[(\w+)\]\]/g, (_match, key) => {
    const val = config[key]
    const str = val !== undefined && val !== null ? String(val) : ''
    return encodeURIComponent(str)
  })
}

/**
 * Remove query parameters that are in keys and have an empty value.
 * @deprecated Prefer stripAllEmptyQueryParams so empty params are always omitted.
 */
export function stripEmptyQueryParams(url: string, keys: string[]): string {
  if (keys.length === 0) { return url }
  try {
    const u = new URL(url)
    const toRemove = new Set(keys.map(k => k.toLowerCase()))
    const keysToDelete: string[] = []
    u.searchParams.forEach((value, key) => {
      if (toRemove.has(key.toLowerCase()) && value === '') {
        keysToDelete.push(key)
      }
    })
    keysToDelete.forEach(key => u.searchParams.delete(key))
    return u.toString()
  }
  catch {
    return url
  }
}

/**
 * Remove all query parameters that have an empty value.
 * Default behavior for URL widgets: params with no default or value are omitted from the query string.
 */
export function stripAllEmptyQueryParams(url: string): string {
  try {
    const u = new URL(url)
    const keysToDelete: string[] = []
    u.searchParams.forEach((value, key) => {
      if (value === '') { keysToDelete.push(key) }
    })
    keysToDelete.forEach(key => u.searchParams.delete(key))
    return u.toString()
  }
  catch {
    return url
  }
}

/**
 * For keys in flagKeys: when value is truthy, output param as key-only (no =value); when falsy, remove param.
 * Used for URL widgets when paramKeysFlag is set.
 */
export function applyFlagParams(url: string, flagKeys: string[]): string {
  if (flagKeys.length === 0) { return url }
  try {
    const u = new URL(url)
    const flagSet = new Set(flagKeys.map(k => k.toLowerCase()))
    const parts: string[] = []
    u.searchParams.forEach((value, key) => {
      if (flagSet.has(key.toLowerCase())) {
        if (value !== '' && value !== 'false' && value !== '0') {
          parts.push(encodeURIComponent(key))
        }
      }
      else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      }
    })
    u.search = parts.length > 0 ? `?${parts.join('&')}` : ''
    return u.toString()
  }
  catch {
    return url
  }
}

function isTruthyFlag(v: unknown): boolean {
  if (v === undefined || v === null) { return false }
  const s = String(v)
  return s !== '' && s !== 'false' && s !== '0'
}

/**
 * Appends flag params that are truthy in config but missing from the URL (e.g. when urlTemplate was saved before the flag was added).
 * Call after substituteUrlTemplate and applyFlagParams so locked/default flags like kiosk always appear when true.
 */
export function appendMissingFlagParams(
  url: string,
  flagKeys: string[],
  config: Record<string, unknown>
): string {
  if (flagKeys.length === 0) { return url }
  try {
    const u = new URL(url)
    const existing = new Set(Array.from(u.searchParams.keys()).map(k => k.toLowerCase()))
    const toAppend: string[] = []
    for (const key of flagKeys) {
      if (existing.has(key.toLowerCase())) { continue }
      if (!isTruthyFlag(config[key])) { continue }
      toAppend.push(encodeURIComponent(key))
    }
    if (toAppend.length === 0) { return url }
    const sep = u.search.length === 0 ? '?' : '&'
    u.search = u.search + sep + toAppend.join('&')
    return u.toString()
  }
  catch {
    return url
  }
}

/**
 * Bulletproof: append any key from fixedDefaults that looks like a truthy flag and is missing from the URL.
 * Use this as a final step so locked flags (e.g. kiosk) always appear even if merge/template/config failed.
 */
export function appendFixedDefaultFlags(
  url: string,
  fixedDefaults: Record<string, unknown> | null | undefined,
  flagKeys: string[] | null | undefined
): string {
  if (!fixedDefaults || typeof fixedDefaults !== 'object' || Object.keys(fixedDefaults).length === 0) { return url }
  const flagSet = new Set((flagKeys ?? []).map(k => k.toLowerCase()))
  try {
    const u = new URL(url)
    const existing = new Set(Array.from(u.searchParams.keys()).map(k => k.toLowerCase()))
    const toAppend: string[] = []
    for (const [key, val] of Object.entries(fixedDefaults)) {
      if (existing.has(key.toLowerCase())) { continue }
      const isFlag = flagSet.has(key.toLowerCase()) || val === 'true' || val === 'false' || val === true || val === false
      if (!isFlag) { continue }
      if (!isTruthyFlag(val)) { continue }
      toAppend.push(encodeURIComponent(key))
    }
    if (toAppend.length === 0) { return url }
    const sep = u.search.length === 0 ? '?' : '&'
    u.search = u.search + sep + toAppend.join('&')
    return u.toString()
  }
  catch {
    return url
  }
}
